import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  FC,
  ReactNode,
  useCallback,
} from "react"
import _ from "lodash"
import { IndoorMap } from "components/IndoorMap"
import { useVenue } from "providers/venue/"
import { IndoorMapContext } from "./IndoorMapContext"
import config from "config"
import { useTranslation } from "providers/i18n"
import { IBasemapConfig } from "services/venue"
import { MapViewType } from "./types"

const FALLBACK_CENTER: [number, number] = [100.5017051, 13.7572619]
const DEFAULT_ZOOM = 18.5
const DEFAULT_MIN_ZOOM: number | null = null
const DEFAULT_MAX_ZOOM = 22
const DEFAULT_MOBILE_PIXEL_RATIO = 1
const DEFAULT_KIOSK_PIXEL_RATIO = 2
const DEFAULT_PITCH = 45
const DEFAULT_BEARING = 0

type IndoorMapProviderProps = {
  children: ReactNode
}

export const IndoorMapProvider: FC<IndoorMapProviderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const [mapLoaded, setMapLoaded] = useState<boolean>(false)
  const [mapLoading, setMapLoading] = useState<boolean>(false)
  const [mapCreated, setMapCreated] = useState<boolean>(false)
  const isInitialized = useRef<boolean>(false)
  const indoorRef = useRef<IndoorMap | null>(null)
  const { venues, mapTheme, mapConfig, configLoaded } = useVenue()

  const defaultCenter = useMemo(() => {
    return _.get(
      venues,
      "[0].properties.display_point.coordinates",
      FALLBACK_CENTER
    ) as [number, number]
  }, [venues])

  const defaultPitch = useMemo(() => {
    return _.get(mapConfig, "default_pitch", DEFAULT_PITCH) as number
  }, [mapConfig])

  const defaultBearing = useMemo(() => {
    return _.get(mapConfig, "default_bearing", DEFAULT_BEARING) as number
  }, [mapConfig])

  const zoomOptions = useMemo(() => {
    const minZoom = _.get(mapConfig, "min_zoom", DEFAULT_MIN_ZOOM) as
      | number
      | null
    const maxZoom = _.get(mapConfig, "max_zoom", DEFAULT_MAX_ZOOM) as number
    return minZoom < maxZoom
      ? { minZoom, maxZoom }
      : { minZoom: DEFAULT_MIN_ZOOM, maxZoom: DEFAULT_MAX_ZOOM }
  }, [mapConfig])

  const minZoom = useMemo(() => zoomOptions.minZoom, [zoomOptions])
  const maxZoom = useMemo(() => zoomOptions.maxZoom, [zoomOptions])

  const basemap = useMemo(
    () => _.get(mapConfig, "basemap", {}) as IBasemapConfig,
    [mapConfig]
  )
  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )
  const pixelRatio = useMemo(() => {
    return isKioskMode
      ? (_.get(
          mapConfig,
          "kiosk_pixel_ratio",
          DEFAULT_KIOSK_PIXEL_RATIO
        ) as number)
      : (_.get(
          mapConfig,
          "mobile_pixel_ratio",
          DEFAULT_MOBILE_PIXEL_RATIO
        ) as number)
  }, [mapConfig, isKioskMode])

  /** Methods */
  const resetMapState = useCallback(() => {
    const indoorMap = indoorRef.current
    if (indoorMap && mapLoaded) {
      indoorMap.clearNavigationGeometries()
      indoorMap.enableClick()
      indoorMap.setFeatureObject3DsOpacity(1)
      indoorMap.setSpriteMarkersOpacity(1)
      indoorMap.showVenueObjects()
      indoorMap.clearHighlightElements()
      indoorMap.clearHighlightObject()
      indoorMap.enableLayersFadingOnZoom()
      return () => {
        if (indoorMap) indoorMap.disableLayersFadingOnZoom()
      }
    }
  }, [indoorRef, mapLoaded])

  const changeLevelByOrdinal = useCallback(
    (ordinal) => {
      const indoorMap = indoorRef.current
      if (indoorMap) indoorMap.changeLevelByOrdinal(ordinal)
    },
    [indoorRef]
  )

  const flyTo = useCallback(
    (mapView: MapViewType) => {
      if (!mapLoaded) return

      const { zoom, bearing, center, pitch } = mapView || {}

      const indoorMap = indoorRef.current
      indoorMap.flyTo(center, {
        zoom,
        bearing,
        pitch,
      })
    },
    [mapLoaded]
  )

  /**
   * Effects
   */

  // Effect 1: Create IndoorMap on didMount
  useEffect(() => {
    const indoorMap = new IndoorMap("map", {
      center: FALLBACK_CENTER,
      defaultZoom: DEFAULT_ZOOM,
      onMapLoading: () => {
        setMapLoading(true)
      },
      onMapReady: (e) => {
        setMapLoaded(true)
        setMapLoading(false)
        // TODO : Replace with hide all geometry later
        indoorRef.current?.changeLevelByOrdinal(0)
      },
    })
    indoorRef.current = indoorMap
    if (indoorRef.current instanceof IndoorMap) setMapCreated(true)
    return () => {
      indoorRef.current = null
      indoorMap.map.remove()
    }
  }, [])

  // Effect 2: set map properties when receiving
  useEffect(() => {
    const indoorMap = indoorRef.current
    if (
      !isInitialized.current &&
      configLoaded &&
      mapCreated &&
      indoorMap instanceof IndoorMap
    ) {
      indoorMap.defaultCenter = defaultCenter
      indoorMap.defaultPitch = defaultPitch
      indoorMap.setCenter(defaultCenter)
      indoorMap.pixelRatio = pixelRatio
      indoorMap.mapConfig = mapConfig
      indoorMap.minZoom = minZoom
      indoorMap.maxZoom = maxZoom
      indoorMap.defaultBearing = defaultBearing
      indoorMap.locale = i18n.resolvedLanguage
      indoorMap.basemap = basemap
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configLoaded,
    defaultCenter,
    defaultPitch,
    mapCreated,
    mapConfig,
    minZoom,
    maxZoom,
    pixelRatio,
    defaultBearing,
    basemap,
  ])

  // Update map locale.
  useEffect(() => {
    const indoorMap = indoorRef.current
    if (indoorMap) {
      const currentLanguage = i18n?.resolvedLanguage
      indoorMap.locale = currentLanguage
    }
  }, [i18n.resolvedLanguage])

  const value = {
    indoorRef,
    mapLoaded,
    mapLoading,
    mapCreated,
    mapTheme,
    defaultBearing,
    defaultPitch,

    // Methods
    resetMapState,
    changeLevelByOrdinal,
    flyTo,
  }

  return (
    <IndoorMapContext.Provider value={value}>
      {children}
    </IndoorMapContext.Provider>
  )
}
